import axios from 'axios';

import { ApiCoordinates } from './../api/types/device';
import { ActionContext } from 'vuex';

import api from '@/api';
import { ApiDeviceList, ApiDeviceListItem, ApiDeviceDates, ApiDeviceRoutes } from '@/api/types/device';
import saveKmlXmlFile from '@/lib/saveKmlXmlFile';
import { Sorting, SortingDir } from '@/types/sorting';
import { computeDistance } from '@/pages/maps/devices-map/map/Map';

export const deviceGenerateStatus = (
    item: ApiDeviceListItem,
    deviceInactiveTime: number,
    deviceWithoutContactTime: number,
): string => {
    if (
        item.Active === true &&
        typeof item.LastContact !== 'undefined' &&
        item.LastContact !== null &&
        item.LastContact
    ) {
        const refreshData = new Date(); // refreshDataTime
        const shiftedInactive = new Date(item.LastContact); // refreshDataTime
        shiftedInactive.setMinutes(shiftedInactive.getMinutes() + deviceInactiveTime);
        const shiftedWithoutContact = new Date(item.LastContact); // refreshDataTime
        shiftedWithoutContact.setMinutes(shiftedWithoutContact.getMinutes() + deviceWithoutContactTime);

        /* const lastContactDate = new Date(item.LastContact); */

        if (shiftedWithoutContact.getTime() <= refreshData.getTime()) {
            return 'dead';
        } else if (shiftedInactive.getTime() < refreshData.getTime()) {
            return 'offline';
            // Red dot: LastContact-Property >= refreshDataTime + "device-without-contact"
        } else if (shiftedInactive.getTime() >= refreshData.getTime()) {
            return 'online';
            // Orange dot: LastContact-Property > refreshDataTime + "device-inactivity"
        } else {
            return 'dead';
        }
    }
    return 'dead';
};

export enum FactorZoom {
    LastPosLow = 'LastPosLow',
    LastPosMedium = 'LastPosMedium',
    LastPosHeight = 'LastPosHeight',
}

export enum DataTab {
    Devices = 'Devices',
    Groupes = 'Groupes',
}

export interface DeviceType {
    Code: string;
    CaptionKey: string;
    Caption: string;
}
export interface DeviceGroups {
    Code: string;
    Id: string;
    CaptionKey: string;
    Groups: Array<string>;
    active: boolean;
}
export interface DeviceToUpdate {
    Name: string;
    Description: string;
    MapZoom: FactorZoom;
    fontColor: string;
    fontBackgroundColor: string;
}

export interface FilteredSearchLocation {
    name: string | null | undefined;
    latLng: google.maps.LatLng | null;
}

interface DevicesState {
    list: ApiDeviceList;
    listRequestPending: boolean;
    dates: ApiDeviceDates;
    datesRequestPending: boolean;
    routesDate: Date;
    routes: ApiDeviceRoutes;
    routesRequestPending: boolean;
    listSorting: string;
    listSortingDir: string;
    listFilterDate: Date;
    listFilters: {
        [key: string]: null | string | Array<string> | number | Array<number>;
    };
    locationFilter: FilteredSearchLocation;
    listSearchFilter: string;
    listSortingOptions: Array<Sorting>;
    cardType: DataTab;
}

const device = {
    namespaced: true,
    state: {
        list: {},
        listRequestPending: false,
        listSorting: Sorting.NAME, // name | time | type
        listSortingDir: SortingDir.ASC, // asc | desc
        listFilterDate: new Date(new Date().setHours(0, 0, 0, 0)),
        listFilters: {
            status: null, // null | dead | no | yes
            types: [],
            groups: [],
        },
        listSortingOptions: [Sorting.NAME /* , Sorting.TIME, Sorting.TYPE */],
        dates: {},
        datesRequestPending: false,
        routesDate: new Date(new Date().setHours(0, 0, 0, 0)),
        routes: {},
        routesRequestPending: false,
        listSearchFilter: '',
        locationFilter: {
            name: null,
            latLng: null,
        },
        cardType: DataTab.Devices,
    },
    getters: {
        itemsSortedFiltered(state: DevicesState): Array<ApiDeviceList> {
            if (typeof state.list.List === 'undefined' || !state.list.List || state.list.List.length === 0) {
                return [];
            }

            let items = JSON.parse(JSON.stringify(state.list.List));

            if (state.listFilters.status !== null) {
                items = items.filter((item: ApiDeviceListItem) => {
                    const itemStatus = item.uiStatus;
                    return itemStatus === state.listFilters.status;
                });
            }
            if (state.listFilters.groups && (state.listFilters.groups as number[]).length > 0) {
                items = items.filter((item: ApiDeviceListItem) => {
                    // https://stackoverflow.com/questions/16312528/check-if-an-array-contains-any-element-of-another-array-in-javascript
                    return (state.listFilters.groups as number[]).some((r) => item.Groups?.includes(r));
                });
            }
            if (state.listFilters.types && (state.listFilters.types as string[]).length > 0) {
                items = items.filter((item: ApiDeviceListItem) => {
                    return (state.listFilters.types as string[]).includes(item.HWT?.Code);
                });
            }

            if (state.locationFilter.latLng) {
                items = items
                    .map((item: ApiDeviceListItem) => ({
                        ...item,
                        distanceToLocation: computeDistance(
                            {
                                lat: item.LastPos?.Lat,
                                lng: item.LastPos?.Lng,
                            },
                            state.locationFilter.latLng,
                        ),
                        overviewPathToLocationFromApi: null,
                    }))
                    .filter((item: ApiDeviceListItem) => item.distanceToLocation !== null);

                items.sort((a: ApiDeviceListItem, b: ApiDeviceListItem) => a.distanceToLocation - b.distanceToLocation);

                items = items.slice(0, 5);

                items.filter((item: ApiDeviceListItem) =>
                    item.Name?.toLocaleLowerCase().includes(state.listSearchFilter?.toLocaleLowerCase()),
                );
            }

            items.sort((a: ApiDeviceListItem, b: ApiDeviceListItem) => {
                const dirModifier = state.listSortingDir === SortingDir.DESC ? -1 : 1;

                if (state.listSorting === Sorting.TIME) {
                    let aTime = 0;
                    let bTime = 0;
                    if (typeof a.LastPos !== 'undefined' && typeof a.LastPos.EvtDate !== 'undefined') {
                        aTime = new Date(a.LastPos.EvtDate).getTime();
                    }
                    if (typeof b.LastPos !== 'undefined' && typeof b.LastPos.EvtDate !== 'undefined') {
                        bTime = new Date(b.LastPos.EvtDate).getTime();
                    }

                    return (aTime - bTime) * dirModifier;
                } else if (state.listSorting === Sorting.TYPE) {
                    return (a.HWT.Code > b.HWT.Code ? 1 : b.HWT.Code > a.HWT.Code ? -1 : 0) * dirModifier;
                } else if (state.listSorting === Sorting.DISTANCE) {
                    if (a.distanceToLocationFromApi === null) {
                        return (
                            (a.distanceToLocation > b.distanceToLocation
                                ? 1
                                : b.distanceToLocation > a.distanceToLocation
                                ? -1
                                : 0) * dirModifier
                        );
                    } else {
                        return (
                            (a.distanceToLocationFromApi > b.distanceToLocationFromApi
                                ? 1
                                : b.distanceToLocationFromApi > a.distanceToLocationFromApi
                                ? -1
                                : 0) * dirModifier
                        );
                    }
                } else {
                    return (a.Name > b.Name ? 1 : b.Name > a.Name ? -1 : 0) * dirModifier;
                }
            });

            return items.filter((item: ApiDeviceListItem) =>
                item?.Name?.toLocaleLowerCase().includes(state.listSearchFilter.toLocaleLowerCase()),
            );
        },
        items(state: DevicesState): Array<ApiDeviceList> {
            if (typeof state.list.List === 'undefined' || !state.list.List || state.list.List.length === 0) {
                return [];
            }

            const items = JSON.parse(JSON.stringify(state.list.List));

            return items;
        },
        types(state: DevicesState): Array<DeviceType> {
            if (typeof state.list.List === 'undefined' || !state.list.List || state.list.List.length === 0) {
                return [];
            }

            const types: Array<DeviceType> = [];
            const codes: Array<string> = [];
            for (const item of state.list.List) {
                if (typeof item.HWT !== 'undefined' && !!item.HWT && typeof item.HWT.Code !== 'undefined') {
                    if (!codes.includes(item.HWT.Code)) {
                        codes.push(item.HWT.Code);
                        types.push({
                            Code: item.HWT.Code,
                            CaptionKey: item.HWT.CaptionKey,
                            Caption: item.HWT.Caption,
                        });
                    }
                }
            }

            return types;
        },
        listSearchFilter(state: DevicesState): string {
            return state.listSearchFilter;
        },
        cardType(state: DevicesState): string {
            return state.cardType;
        },
        locationFilter(state: DevicesState): FilteredSearchLocation {
            return state.locationFilter;
        },
        readFilteredGroups(state: DevicesState): null | string | Array<string> | number | Array<number> {
            return state.listFilters.groups;
        },
        readNumberOfDevices(state: DevicesState): number {
            return state.list.RowCount;
        },
    },
    mutations: {
        startListRequest(state: DevicesState): void {
            state.listRequestPending = true;
        },
        endListRequest(state: DevicesState): void {
            state.listRequestPending = false;
        },
        updateList(state: DevicesState, list: ApiDeviceList): void {
            state.list = list;
        },
        updateIconToSvg(state: DevicesState, svgDetails: { svg: string; deviceId: number }): void {
            const deviceList = [...state.list.List];
            const updatedDevice = deviceList.filter((device) => device.Id === svgDetails.deviceId)[0];
            updatedDevice.svgIcon = svgDetails.svg;
            const filtredDeviceList = [...state.list.List].filter((device) => device.Id !== svgDetails.deviceId);
            state.list.List = [...filtredDeviceList, updatedDevice];
        },
        clearList(state: DevicesState): void {
            state.list = {
                RowCount: 0,
            };
        },
        startDatesRequest(state: DevicesState): void {
            state.datesRequestPending = true;
        },
        endDatesRequest(state: DevicesState): void {
            state.datesRequestPending = false;
        },
        updateDates(state: DevicesState, dates: ApiDeviceDates): void {
            state.dates = dates;
        },
        clearDates(state: DevicesState): void {
            state.dates = {};
        },
        updateRoutesDate(state: DevicesState, routesDate: Date): void {
            state.routesDate = routesDate;
        },
        startRoutesRequest(state: DevicesState): void {
            state.routesRequestPending = true;
        },
        endRoutesRequest(state: DevicesState): void {
            state.routesRequestPending = false;
        },
        updateRoutes(state: DevicesState, routes: ApiDeviceRoutes): void {
            state.routes = routes;
        },
        updateCalculatedDistancesToPoint(
            state: DevicesState,
            computeDistance: {
                deviceId: number;
                distance: number;
                overview_path: Array<google.maps.LatLng>;
            },
        ): void {
            if (typeof state.list.List === 'undefined' || !state.list.List || state.list.List.length === 0) {
                return;
            } else {
                const items = JSON.parse(JSON.stringify(state.list.List));
                const indexToUpdate = items.findIndex((item) => item.Id === computeDistance.deviceId);
                items[indexToUpdate].distanceToLocationFromApi = computeDistance.distance;
                items[indexToUpdate].overviewPathToLocationFromApi = computeDistance.overview_path;
                state.list.List = items;
            }
        },
        clearRoutes(state: DevicesState): void {
            state.routes = {};
        },
        clearFilters(state: DevicesState): void {
            state.listRequestPending = false;
            state.listSorting = Sorting.NAME; // name | time | type
            state.listSortingDir = SortingDir.ASC; // asc | desc
            state.listFilterDate = new Date(new Date().setHours(0, 0, 0, 0));
            state.listFilters = {
                status: null, // null | dead | no | yes
                types: [],
                groups: [],
            };
            state.listSortingOptions = [Sorting.NAME];
            state.listSearchFilter = '';
            state.locationFilter = {
                name: null,
                latLng: null,
            };
        },
        updateListSorting(state: DevicesState, sort: string): void {
            state.listSorting = sort;
        },
        updateListSortingDir(state: DevicesState, dir: string): void {
            state.listSortingDir = dir;
        },
        updateListFilterDate(state: DevicesState, date: Date): void {
            state.listFilterDate = date;
        },
        updateListFilters(
            state: DevicesState,
            { field, value }: { field: string; value: string | number | string[] | number[] },
        ): void {
            state.listFilters[field] = value;
        },
        updateLocationFilter(state: DevicesState, location: FilteredSearchLocation): void {
            if (location.name === null) {
                state.listSorting = Sorting.NAME;
                state.listSortingOptions = [Sorting.NAME];
            } else {
                state.listSortingOptions = [Sorting.NAME, Sorting.DISTANCE];
            }
            state.locationFilter = location;
        },
        updateListSearchFilter(state: DevicesState, searchPhrase: string): void {
            state.listSearchFilter = searchPhrase;
        },
        changeTabType(state: DevicesState, type: DataTab): void {
            state.cardType = type;
        },
    },
    actions: {
        clearList(context: ActionContext<DevicesState, null>): void {
            context.commit('clearList');
        },
        clearFilters(context: ActionContext<DevicesState, null>): void {
            context.commit('clearFilters');
        },
        async list(context: ActionContext<DevicesState, null>, onComplated?: () => void): Promise<void> {
            context.commit('startListRequest');
            try {
                const token = window.localStorage.getItem('authcredentials');
                const response = await api.device.list(token || '');
                const deviceInactive =
                    context.rootGetters['auth/userSettingString'](
                        'usersetting.customer.devicemap.detailpage.map.deviceinactivetime',
                    ) || 120;
                const deviceWithoutContact =
                    context.rootGetters['auth/userSettingString'](
                        'usersetting.customer.devicemap.detailpage.map.devicewithoutcontacttime',
                    ) || 180;
                const deviceData =
                    (response.data?.List &&
                        response.data?.List.map((item) => ({
                            ...item,
                            Groups: item.Groups && item.Groups.length === 0 ? [-1] : item.Groups ? item.Groups : [-1], // create special group for devices without groups
                            temperatures: {
                                Temp01: item?.SensorTemp01 || null,
                                Temp02: item?.SensorTemp02 || null,
                                Temp03: item?.SensorTemp03 || null,
                                Temp04: item?.SensorTemp04 || null,
                            },
                            uiStatus: deviceGenerateStatus(
                                item,
                                parseInt(deviceInactive),
                                parseInt(deviceWithoutContact),
                            ),
                            svgIcon: '',
                        }))) ||
                    [];

                deviceData.map((device) => {
                    if (device.IconName && device.IconColor) {
                        const iconName = device.IconName.includes('png') ? device.IconName.replace(/png/g, 'svg') : device.IconName;
                        axios(`https://portalbeta.tracker.com/api/trk/common/image/icon/svg/${iconName}`).then(
                            (r) =>
                                context.commit('updateIconToSvg', {
                                    svg: r.data,
                                    deviceId: device.Id,
                                }),
                        );
                    }
                });
                context.commit('updateList', {
                    ...response.data,
                    List: [...deviceData],
                });
            } catch (err) {
                // console.log(err);
            } finally {
                onComplated && onComplated();
                context.commit('endListRequest');
            }
        },
        clearDates(context: ActionContext<DevicesState, null>): void {
            context.commit('clearDates');
        },
        async dates(context: ActionContext<DevicesState, null>, deviceId: number): Promise<void> {
            context.commit('startDatesRequest');
            try {
                const token = window.localStorage.getItem('authcredentials');
                const response = await api.device.dates(token || '', deviceId);
                context.commit('updateDates', response.data);

                if (response.data && response.data.Values && response.data.Values.length > 0) {
                    let index = response.data.Values.findIndex((item) => {
                        const date = new Date(item);
                        if (context.state.listFilterDate.getTime() - date.getTime() < 1000 * 60 * 60) {
                            return true;
                        }
                        return false;
                    });
                    if (index === -1) {
                        index = response.data.Values.length - 1;
                    }
                    context.commit('updateRoutesDate', new Date(response.data.Values[index]));
                } else {
                    context.commit('updateRoutesDate', new Date(new Date().setHours(0, 0, 0, 0)));
                }
            } catch (err) {
                // console.log(err);
            } finally {
                context.commit('endDatesRequest');
            }
        },
        async updateDatesWithRoutes(
            context: ActionContext<DevicesState, null>,
            { deviceId, date }: { deviceId: number; date: Date },
        ): Promise<void> {
            context.commit('updateRoutesDate', date);
            await context.dispatch('routes', deviceId);
        },
        clearRoutes(context: ActionContext<DevicesState, null>): void {
            context.commit('clearRoutes');
        },
        async routes(context: ActionContext<DevicesState, null>, deviceId: number): Promise<void> {
            context.commit('startRoutesRequest');
            try {
                const token = window.localStorage.getItem('authcredentials');
                const response = await api.device.routes(token || '', deviceId, context.state.routesDate);

                context.commit('updateRoutes', response.data);
            } catch (err) {
                // console.log(err);
            } finally {
                context.commit('endRoutesRequest');
            }
        },
        async updateListFilterDate(context: ActionContext<DevicesState, null>, fromDate: Date): Promise<void> {
            context.commit('updateListFilterDate', fromDate);
            await context.dispatch('list'); // TODO: list needs to use the date
        },
        async exportAllDevices(context: ActionContext<DevicesState, null>, { date }: { date: string }): Promise<void> {
            try {
                const token = window.localStorage.getItem('authcredentials');
                await api.device
                    .exportAll(token || '', date)
                    .then((res) => res.data)
                    .then(saveKmlXmlFile);
            } catch (err) {
                // console.log(err);
            }
        },
        async exportSingleDevice(
            context: ActionContext<DevicesState, null>,
            { date, deviceId }: { date: string; deviceId: number },
        ): Promise<void> {
            try {
                const token = window.localStorage.getItem('authcredentials');
                await api.device
                    .exportSingle(token || '', date, deviceId)
                    .then((res) => res.data)
                    .then(saveKmlXmlFile);
            } catch (err) {
                // console.log(err);
            }
        },
        async updateDeviceById(
            context: ActionContext<DevicesState, null>,
            { data, deviceId }: { data: DeviceToUpdate; deviceId: number },
        ): Promise<void> {
            try {
                const token = window.localStorage.getItem('authcredentials');
                await api.device.updateSingle(token || '', data, deviceId);
            } catch (err) {
                // console.log(err);
            }
        },
        async updateDistanceToLocation(
            context: ActionContext<DevicesState, null>,
            {
                id,
                latLngDevice,
                latLngPoint,
            }: { id: number; latLngDevice: ApiCoordinates; latLngPoint: google.maps.LatLng },
        ): Promise<void> {
            try {
                const directionsService = new google.maps.DirectionsService();
                directionsService.route(
                    {
                        origin: { lat: latLngDevice.Lat, lng: latLngDevice.Lng },
                        destination: { lat: latLngPoint.lat(), lng: latLngPoint.lng() },
                        travelMode: google.maps.TravelMode['DRIVING'],
                    },
                    (response, status) => {
                        if (status === 'OK') {
                            /* console.log({
                                distance: response.routes[0].legs[0].distance.value,
                                overview_path: response.routes[0].overview_path,
                                deviceId: id,
                            }); */
                            context.commit('updateCalculatedDistancesToPoint', {
                                distance: response.routes[0].legs[0].distance.value,
                                overview_path: response.routes[0].overview_path,
                                deviceId: id,
                            });
                        }
                    },
                );
            } catch (err) {
                // console.log(err);
            }
        },
    },
};

export default device;
